import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { GetList, AddItem, UpdateItem, DeleteItem } from '../../REST/crud';
import { useForm, Controller } from "react-hook-form";
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import cronstrue from 'cronstrue';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import ResponsiveDataGrid from '../../themes/components/ResponsiveDataGrid';
import WorkflowDialogShell from '../../themes/components/WorkflowDialogShell';

// ─── cron helpers ────────────────────────────────────────────────────────────

const WEEK_DAYS = [
    { label: 'Sun', value: 0 }, { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 }, { label: 'Thu', value: 4 }, { label: 'Fri', value: 5 }, { label: 'Sat', value: 6 },
];

function buildExpression({ period, minute, hour, intervalMinutes, intervalHours, dayOfMonth, weekDays }) {
    const m = minute ?? 0;
    const h = hour ?? 0;
    switch (period) {
        case 'minute': return `*/${intervalMinutes || 1} * * * *`;
        case 'hour':   return `${m} */${intervalHours || 1} * * *`;
        case 'day':    return `${m} ${h} * * *`;
        case 'week':   return weekDays && weekDays.length > 0 ? `${m} ${h} * * ${weekDays.join(',')}` : `${m} ${h} * * *`;
        case 'month':  return `${m} ${h} ${dayOfMonth || 1} * *`;
        default:       return `${m} ${h} * * *`;
    }
}

function parseExpression(expr) {
    const def = { period: 'day', minute: 0, hour: 0, intervalMinutes: 1, intervalHours: 1, dayOfMonth: 1, weekDays: [] };
    if (!expr) return def;
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) return def;
    const [min, hr, dom, , dow] = parts;
    if (min.startsWith('*/') && hr === '*' && dom === '*' && dow === '*')
        return { ...def, period: 'minute', intervalMinutes: parseInt(min.slice(2)) || 1 };
    if (hr.startsWith('*/') && dom === '*' && dow === '*')
        return { ...def, period: 'hour', intervalHours: parseInt(hr.slice(2)) || 1, minute: parseInt(min) || 0 };
    if (dom === '*' && dow !== '*')
        return { ...def, period: 'week', minute: parseInt(min) || 0, hour: parseInt(hr) || 0, weekDays: dow.split(',').map(Number) };
    if (dom !== '*' && dow === '*' && hr !== '*')
        return { ...def, period: 'month', minute: parseInt(min) || 0, hour: parseInt(hr) || 0, dayOfMonth: parseInt(dom) || 1 };
    return { ...def, period: 'day', minute: parseInt(min) || 0, hour: parseInt(hr) || 0 };
}

function CronPicker({ value, onChange }) {
    const [state, setState] = useState(() => parseExpression(value || '0 0 * * *'));

    useEffect(() => {
        if (value) setState(parseExpression(value));
    }, [value]);

    const update = (patch) => {
        const next = { ...state, ...patch };
        setState(next);
        onChange(buildExpression(next));
    };

    const expr = buildExpression(state);
    let humanized = '';
    try { humanized = cronstrue.toString(expr); } catch (_) {}

    const numOptions = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => i + from);

    return (
        <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 150 }}>
                <InputLabel>Period</InputLabel>
                <Select value={state.period} label="Period" onChange={e => update({ period: e.target.value })}>
                    <MenuItem value="minute">Every N minutes</MenuItem>
                    <MenuItem value="hour">Every N hours</MenuItem>
                    <MenuItem value="day">Daily</MenuItem>
                    <MenuItem value="week">Weekly</MenuItem>
                    <MenuItem value="month">Monthly</MenuItem>
                </Select>
            </FormControl>

            {state.period === 'minute' && (
                <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 110 }}>
                    <InputLabel>Every</InputLabel>
                    <Select value={state.intervalMinutes || 1} label="Every" onChange={e => update({ intervalMinutes: e.target.value })}>
                        {numOptions(1, 59).map(n => <MenuItem key={n} value={n}>{n} min{n > 1 ? 's' : ''}</MenuItem>)}
                    </Select>
                </FormControl>
            )}

            {state.period === 'hour' && (
                <>
                    <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 110 }}>
                        <InputLabel>Every</InputLabel>
                        <Select value={state.intervalHours || 1} label="Every" onChange={e => update({ intervalHours: e.target.value })}>
                            {numOptions(1, 23).map(n => <MenuItem key={n} value={n}>{n} hr{n > 1 ? 's' : ''}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 110 }}>
                        <InputLabel>At minute</InputLabel>
                        <Select value={state.minute ?? 0} label="At minute" onChange={e => update({ minute: e.target.value })}>
                            {numOptions(0, 59).map(n => <MenuItem key={n} value={n}>{String(n).padStart(2, '0')}</MenuItem>)}
                        </Select>
                    </FormControl>
                </>
            )}

            {(state.period === 'day' || state.period === 'week' || state.period === 'month') && (
                <>
                    <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 90 }}>
                        <InputLabel>Hour</InputLabel>
                        <Select value={state.hour ?? 0} label="Hour" onChange={e => update({ hour: e.target.value })}>
                            {numOptions(0, 23).map(n => <MenuItem key={n} value={n}>{String(n).padStart(2, '0')}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 90 }}>
                        <InputLabel>Minute</InputLabel>
                        <Select value={state.minute ?? 0} label="Minute" onChange={e => update({ minute: e.target.value })}>
                            {numOptions(0, 59).map(n => <MenuItem key={n} value={n}>{String(n).padStart(2, '0')}</MenuItem>)}
                        </Select>
                    </FormControl>
                </>
            )}

            {state.period === 'week' && (
                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Days of week</Typography>
                    <ToggleButtonGroup value={state.weekDays || []} onChange={(_, val) => update({ weekDays: val })} size="small">
                        {WEEK_DAYS.map(d => (
                            <ToggleButton key={d.value} value={d.value} sx={{ px: 1.2, py: 0.4, fontSize: 11 }}>{d.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            )}

            {state.period === 'month' && (
                <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 100 }}>
                    <InputLabel>Day of month</InputLabel>
                    <Select value={state.dayOfMonth || 1} label="Day of month" onChange={e => update({ dayOfMonth: e.target.value })}>
                        {numOptions(1, 31).map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </Select>
                </FormControl>
            )}

            <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Expression: </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{expr}</Typography>
                {humanized && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{humanized}</Typography>}
            </Box>
        </Box>
    );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function InvoiceCycles() {
    const [mode, setMode] = useState(null)
    const [refreshKey, setRefreshKey] = useState(0)
    const navigate = useNavigate()

    const getDesc = (val) => {
        try {
            return cronstrue.toString(val);
        }
        catch (ex) {
            console.log(ex, val)
            return 'NA'
        }
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 0.3, minVisible: 'xs' },
        { field: 'description', headerName: 'Description', flex: 0.3, minVisible: 'md' },
        { field: 'expression', headerName: 'Schedule', flex: 0.4, minVisible: 'xs', valueGetter: (params) => getDesc(params.row.expression) },
        {
            field: 'actions', headerName: 'Actions', flex: 0.2, minVisible: 'xs', renderCell: (params) => {
                return <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id) }}><DeleteIcon /></IconButton>
            }
        }
    ];

    const fetchInvoiceCycles = useCallback(async (page, limit, filters, sort) => {
        const params = {
            page,
            limit
        }
        
        if (Object.keys(filters).length > 0) {
            params.filters = JSON.stringify(filters)
        }
        
        if (sort) {
            params.sort = JSON.stringify(sort)
        }
        
        const result = await GetList('InvoiceCycles', params);

        if (result.data?.data) {
            return {
                data: result.data.data,
                pagination: result.data.pagination
            };
        }

        if (Array.isArray(result.data)) {
            return {
                data: result.data,
                pagination: { total: result.data.length, page: 1, totalPages: 1 }
            };
        }

        return { data: [], pagination: { total: 0, page: 1, totalPages: 0 } };
    }, []);

    const handleMode = (mode) => { setMode(mode) }

    const handleData = (data) => {
        const payload = { ...data, dueDays: Number(data.dueDays) };
        const action = mode?._id ? UpdateItem('InvoiceCycles', mode._id, payload) : AddItem('InvoiceCycles', payload);
        action.then(() => {
            setMode(null);
            setRefreshKey(k => k + 1);
        });
    }

    const handleDelete = (id) => {
        DeleteItem('InvoiceCycles', id).then(() => {
            setRefreshKey(k => k + 1);
        })
    }

    return <Fragment>
        <PageShell>
            <PageHeader
                title="Invoice Cycles"
                back
                onBack={() => navigate(-1)}
                actions={
                    <Action
                        primary
                        icon={<AddIcon />}
                        label="Add Invoice Cycle"
                        onClick={() => setMode({})}
                    />
                }
            />

            {mode !== null && <FormDialog key={mode?._id || 'new'} open data={mode} handleClose={() => setMode(null)} handleSave={handleData} />}

            <PageContent fill>
                <ResponsiveDataGrid
                    key={refreshKey}
                    columns={columns}
                    fetchData={fetchInvoiceCycles}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowId={(i) => i?._id}
                    onRowClick={(row) => setMode(row)}
                />
            </PageContent>
        </PageShell>
    </Fragment>
}



function FormDialog({ open = false, data, handleClose, handleSave }) {
    const schema = yup.object({
        name: yup.string().trim().required(),
        description: yup.string().trim().required(),
        expression: yup.string().trim().required()
    }).required();

    const { control, handleSubmit, formState: { errors }, reset } = useForm({
        defaultValues: {
            name: data?.name ?? '',
            description: data?.description ?? '',
            dueDays: data?.dueDays != null ? String(data.dueDays) : '',
            expression: data?.expression ?? '0 0 * * *'
        },
        resolver: yupResolver(schema)
    });

    // Force reset after mount so MUI labels reflect the initial values
    useEffect(() => {
        reset({
            name: data?.name ?? '',
            description: data?.description ?? '',
            dueDays: data?.dueDays != null ? String(data.dueDays) : '',
            expression: data?.expression ?? '0 0 * * *'
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'


    return <WorkflowDialogShell
        open={open}
        onClose={handleClose}
        onSave={handleSubmit(handleSave)}
        title={data?._id ? 'Edit Invoice Cycle' : 'Add Invoice Cycle'}
    >
        <>
            <Controller name="name" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.name)} helperText={errors.name && errors.name.message} fullWidth label='Name' InputLabelProps={{ shrink: true }} {...field} />} />
            <Controller name="description" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.description)} helperText={errors.description && errors.description.message} fullWidth label='Description' InputLabelProps={{ shrink: true }} {...field} />} />
            <Controller name="dueDays" control={control} render={({ field }) => <TextField margin='dense' type="number"
                error={isError(errors.dueDays)} helperText={errors.dueDays && errors.dueDays.message} fullWidth label='Due Days' InputLabelProps={{ shrink: true }} {...field} />} />
            <Controller name="expression" control={control} render={({ field }) => (
                <CronPicker value={field.value} onChange={field.onChange} />
            )} />
        </>
    </WorkflowDialogShell>
}
